// AI Provider abstraction — supports Claude, OpenAI, and OpenAI-compatible APIs with streaming

const SETTINGS_KEY = 'scadplus-ai-settings';

export type ProviderType = 'claude' | 'openai' | 'deepseek' | 'groq' | 'openrouter' | 'ollama' | 'lmstudio' | 'custom';

export interface ProviderConfig {
  label: string;
  baseUrl: string;
  defaultModel: string;
  sseFormat: 'claude' | 'openai';
  needsAuth: boolean;
  /** Whether the provider's chat API accepts image input (model permitting). */
  supportsImages: boolean;
  extraHeaders?: Record<string, string>;
  hint: string;
}

export const PROVIDER_CONFIGS: Record<ProviderType, ProviderConfig> = {
  claude: {
    label: 'Claude (Anthropic)',
    baseUrl: 'https://api.anthropic.com/v1',
    defaultModel: 'claude-sonnet-4-5-20250929',
    sseFormat: 'claude',
    needsAuth: true,
    supportsImages: true,
    hint: 'Get your API key at console.anthropic.com',
  },
  openai: {
    label: 'OpenAI',
    baseUrl: 'https://api.openai.com/v1',
    defaultModel: 'gpt-4o',
    sseFormat: 'openai',
    needsAuth: true,
    supportsImages: true,
    hint: 'Get your API key at platform.openai.com',
  },
  deepseek: {
    label: 'DeepSeek',
    baseUrl: 'https://api.deepseek.com/v1',
    defaultModel: 'deepseek-chat',
    sseFormat: 'openai',
    needsAuth: true,
    supportsImages: false,
    hint: 'Get your API key at platform.deepseek.com',
  },
  groq: {
    label: 'Groq',
    baseUrl: 'https://api.groq.com/openai/v1',
    defaultModel: 'llama-3.3-70b-versatile',
    sseFormat: 'openai',
    needsAuth: true,
    supportsImages: false,
    hint: 'Get your API key at console.groq.com — fast inference',
  },
  openrouter: {
    label: 'OpenRouter',
    baseUrl: 'https://openrouter.ai/api/v1',
    defaultModel: 'anthropic/claude-sonnet-4-5-20250929',
    sseFormat: 'openai',
    needsAuth: true,
    supportsImages: true,
    extraHeaders: { 'HTTP-Referer': 'https://scad.plus' },
    hint: 'Access 100+ models at openrouter.ai',
  },
  ollama: {
    label: 'Ollama (Local)',
    baseUrl: 'http://localhost:11434/v1',
    defaultModel: 'llama3.1',
    sseFormat: 'openai',
    needsAuth: false,
    supportsImages: true,
    hint: 'Runs locally — no API key needed. Install from ollama.com',
  },
  lmstudio: {
    label: 'LM Studio (Local)',
    baseUrl: 'http://localhost:1234/v1',
    defaultModel: '',
    sseFormat: 'openai',
    needsAuth: false,
    supportsImages: true,
    hint: 'Runs locally — no API key needed. Enable CORS in LM Studio server settings.',
  },
  custom: {
    label: 'Custom (OpenAI-compatible)',
    baseUrl: '',
    defaultModel: '',
    sseFormat: 'openai',
    needsAuth: true,
    supportsImages: true,
    hint: 'Enter any OpenAI-compatible API endpoint',
  },
};

export interface AISettings {
  provider: ProviderType;
  apiKey: string;
  model: string;
  baseUrl?: string;
}

export interface ContentBlock {
  type: 'text' | 'image';
  text?: string;
  imageData?: string;  // base64
  mimeType?: string;
}

export interface AIMessage {
  role: 'user' | 'assistant';
  content: string | ContentBlock[];
}

// --- Settings persistence ---

export function loadAISettings(): AISettings | null {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.provider) return null;
      const config = PROVIDER_CONFIGS[parsed.provider as ProviderType];
      if (config && !config.needsAuth) return parsed; // ollama doesn't need key
      if (parsed.apiKey) return parsed;
    }
  } catch { /* ignore */ }
  return null;
}

export function saveAISettings(settings: AISettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

export function clearAISettings(): void {
  localStorage.removeItem(SETTINGS_KEY);
}

// --- Default models ---

export function getDefaultModel(provider: ProviderType): string {
  return PROVIDER_CONFIGS[provider]?.defaultModel || '';
}

// --- Image resize helper ---

export async function resizeImageToBase64(file: File, maxDim: number = 1568): Promise<{ data: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);

      // Determine output format
      const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const dataUrl = canvas.toDataURL(mimeType, 0.85);
      const data = dataUrl.split(',')[1]; // strip data:... prefix
      resolve({ data, mimeType });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

// --- Format messages for each provider ---

function formatClaudeMessages(messages: AIMessage[]): any[] {
  return messages.map(msg => {
    if (typeof msg.content === 'string') {
      return { role: msg.role, content: msg.content };
    }
    // Multi-part content (text + images)
    const content: any[] = [];
    for (const block of msg.content) {
      if (block.type === 'image' && block.imageData && block.mimeType) {
        content.push({
          type: 'image',
          source: { type: 'base64', media_type: block.mimeType, data: block.imageData },
        });
      } else if (block.type === 'text' && block.text) {
        content.push({ type: 'text', text: block.text });
      }
    }
    return { role: msg.role, content };
  });
}

function formatOpenAIMessages(systemPrompt: string, messages: AIMessage[]): any[] {
  const result: any[] = [{ role: 'system', content: systemPrompt }];
  for (const msg of messages) {
    if (typeof msg.content === 'string') {
      result.push({ role: msg.role, content: msg.content });
    } else {
      const content: any[] = [];
      for (const block of msg.content) {
        if (block.type === 'image' && block.imageData && block.mimeType) {
          content.push({
            type: 'image_url',
            image_url: { url: `data:${block.mimeType};base64,${block.imageData}` },
          });
        } else if (block.type === 'text' && block.text) {
          content.push({ type: 'text', text: block.text });
        }
      }
      result.push({ role: msg.role, content });
    }
  }
  return result;
}

// --- SSE stream parser ---

function parseSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  format: 'claude' | 'openai',
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (err: Error) => void,
): void {
  const decoder = new TextDecoder();
  let buffer = '';

  function processLines(): void {
    const lines = buffer.split('\n');
    buffer = lines.pop()!; // keep incomplete line

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const data = line.slice(6).trim();
      if (data === '[DONE]') {
        onDone();
        return;
      }
      try {
        const json = JSON.parse(data);
        if (format === 'claude') {
          // Claude SSE events
          if (json.type === 'content_block_delta' && json.delta?.type === 'text_delta') {
            onChunk(json.delta.text);
          } else if (json.type === 'message_stop') {
            onDone();
            return;
          } else if (json.type === 'error') {
            onError(new Error(json.error?.message || 'Claude API error'));
            return;
          }
        } else {
          // OpenAI-compatible SSE events
          const delta = json.choices?.[0]?.delta;
          if (delta?.content) {
            onChunk(delta.content);
          }
          if (json.choices?.[0]?.finish_reason) {
            onDone();
            return;
          }
        }
      } catch { /* skip malformed JSON lines */ }
    }
  }

  function pump(): void {
    reader.read().then(({ done, value }) => {
      if (done) {
        if (buffer.trim()) processLines(); // process remaining
        onDone();
        return;
      }
      buffer += decoder.decode(value, { stream: true });
      processLines();
      pump();
    }).catch(err => {
      if (err.name === 'AbortError') {
        onDone();
      } else {
        onError(err);
      }
    });
  }

  pump();
}

// --- Main streaming chat function ---

export function streamChat(
  settings: AISettings,
  systemPrompt: string,
  messages: AIMessage[],
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (err: Error) => void,
): AbortController {
  const controller = new AbortController();

  const doFetch = async () => {
    try {
      const config = PROVIDER_CONFIGS[settings.provider];
      const baseUrl = settings.baseUrl || config.baseUrl;
      let url: string;
      let headers: Record<string, string>;
      let body: string;

      if (config.sseFormat === 'claude') {
        url = `${baseUrl}/messages`;
        headers = {
          'Content-Type': 'application/json',
          'x-api-key': settings.apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        };
        body = JSON.stringify({
          model: settings.model,
          max_tokens: 8192,
          stream: true,
          system: systemPrompt,
          messages: formatClaudeMessages(messages),
        });
      } else {
        url = `${baseUrl}/chat/completions`;
        headers = { 'Content-Type': 'application/json' };
        if (config.needsAuth && settings.apiKey) {
          headers['Authorization'] = `Bearer ${settings.apiKey}`;
        }
        if (config.extraHeaders) {
          Object.assign(headers, config.extraHeaders);
        }
        body = JSON.stringify({
          model: settings.model,
          max_tokens: 8192,
          stream: true,
          messages: formatOpenAIMessages(systemPrompt, messages),
        });
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        let errorMsg: string;
        if (response.status === 401) {
          errorMsg = 'Invalid API key. Check your key in AI Settings.';
        } else if (response.status === 429) {
          errorMsg = 'Rate limited. Please wait a moment and try again.';
        } else if (response.status === 400) {
          try {
            const errJson = JSON.parse(errorText);
            errorMsg = errJson.error?.message || `Bad request (${response.status})`;
          } catch {
            errorMsg = `Bad request (${response.status})`;
          }
        } else {
          errorMsg = `API error ${response.status}: ${errorText.slice(0, 200)}`;
        }
        onError(new Error(errorMsg));
        return;
      }

      if (!response.body) {
        onError(new Error('No response body'));
        return;
      }

      const reader = response.body.getReader();
      parseSSEStream(reader, config.sseFormat, onChunk, onDone, onError);
    } catch (err: any) {
      if (err.name === 'AbortError') {
        onDone();
      } else {
        onError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  };

  doFetch();
  return controller;
}

// --- Validate API key ---

export async function validateKey(settings: AISettings): Promise<{ ok: boolean; error?: string }> {
  try {
    const config = PROVIDER_CONFIGS[settings.provider];
    const baseUrl = settings.baseUrl || config.baseUrl;

    if (config.sseFormat === 'claude') {
      // Claude: POST /messages with minimal payload
      const response = await fetch(`${baseUrl}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': settings.apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: settings.model,
          max_tokens: 1,
          messages: [{ role: 'user', content: 'Hi' }],
        }),
      });
      if (response.ok) return { ok: true };
      if (response.status === 401) return { ok: false, error: 'Invalid API key' };
      if (response.status === 429) return { ok: true }; // Rate limited but key is valid
      return { ok: false, error: `Error: ${response.status}` };
    } else if (!config.needsAuth) {
      // Local server (Ollama, LM Studio): just check if server is reachable
      const response = await fetch(`${baseUrl}/models`).catch(() => null);
      if (!response) return { ok: false, error: `Cannot connect to ${config.label}. Is it running?` };
      if (response.ok) return { ok: true };
      return { ok: false, error: `Cannot connect to ${config.label} (${response.status}). Is it running?` };
    } else {
      // OpenAI-compatible: GET /models with Bearer token
      const headers: Record<string, string> = { 'Authorization': `Bearer ${settings.apiKey}` };
      if (config.extraHeaders) Object.assign(headers, config.extraHeaders);
      const response = await fetch(`${baseUrl}/models`, { headers });
      if (response.ok) return { ok: true };
      if (response.status === 401) return { ok: false, error: 'Invalid API key' };
      return { ok: false, error: `Error: ${response.status}` };
    }
  } catch (err: any) {
    if (err instanceof TypeError && err.message === 'Failed to fetch') {
      return { ok: false, error: 'Connection blocked (likely CORS). Enable CORS in your server settings and try again.' };
    }
    return { ok: false, error: 'Network error. Check your connection.' };
  }
}

/** Downscale a data URL (e.g. a canvas capture) to base64 JPEG for chat attachments. */
export async function resizeDataUrlToBase64(dataUrl: string, maxDim: number = 1024): Promise<{ data: string; mimeType: string }> {
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('Could not load image'));
    img.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
  return { data: canvas.toDataURL('image/jpeg', 0.85).split(',')[1], mimeType: 'image/jpeg' };
}
